import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { useLocation } from "react-router-dom";
import { Col, Modal, ModalBody, ModalHeader, Row } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import Badge from "../../../components/Badge";
import PaginationComponent from "../../../components/Pagination";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import { logAdminContext } from "../../../context/AdminContext/LogAdminContext/LogAdminContext";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  convertLogMethodToColor,
  convertLogMethodToText,
  convertLogUrlToText,
  convertRoleToText,
  exporterData,
} from "../../../utils/enum";
import { convertDate } from "../../../utils";
import VerifiedBadge from "../../../components/VerifiedBadge";
import DataExporter from "../../../components/DataExporter";

const LogsAdmin = () => {
  const { pathname } = useLocation();

  const {
    logs,
    log,
    setLog,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    handleGetLogs,
    handleDeleteLog,
    handleDeleteAllLog,
  } = useContext(logAdminContext);

  useEffect(() => {
    handleGetLogs();
  }, [start, end, pageNumber, itemsCount, sort, pathname]);

  return (
    <>
      <SiteTitle title="رویدادنگاری" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          {...(!isEmpty(logs) && {
            firstItem: (
              <button
                title="حذف همه"
                className="btn btn-danger radius-8 d-flex align-items-center"
                onClick={() =>
                  areYouSureMessage(
                    handleDeleteAllLog,
                    `آیا از حذف همه رویدادها مطمئنید؟`
                  )
                }
              >
                <Icon icon="fa:trash" className="text-xl" />
              </button>
            ),
          })}
          {...(!isEmpty(logs) && {
            endItem: (
              <DataExporter
                headers={exporterData.logs.headers}
                keys={exporterData.logs.keys}
                data={logs}
                fileName={exporterData.logs.fileName}
              />
            ),
          })}
        />
        <div className="card-body p-24">
          {isEmpty(logs) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">کاربر</th>
                      <th scope="col">بخش</th>
                      <th scope="col">عملیات</th>
                      <th scope="col">تاریخ</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <UserAvatar
                              className="ms-2"
                              {...(item?.user?.avatar
                                ? { image: item?.user?.avatar }
                                : {
                                    icon: "solar:user-broken",
                                  })}
                            />
                            <div className="flex-grow-1">
                              <span className="text-md mb-0 fw-normal text-secondary-light">
                                {item?.user?.firstName} {item?.user?.lastName}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{convertLogUrlToText(item.pathname)}</td>
                        <td>
                          <Badge
                            className="badge-dim"
                            color={convertLogMethodToColor(item.method)}
                          >
                            {convertLogMethodToText(item.method)}
                          </Badge>
                        </td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm:ss")}
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() => {
                                  setLog(item);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                جزئیات
                              </li>

                              <li className="dropdown-divider" />
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteLog(item._id),
                                    `آیا از حذف رویداد ${item?.user?.firstName} ${item?.user?.lastName} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={!isEmpty(log)}
        className="modal-dialog-centered"
        size="lg"
        onHide={() => setLog({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">جزئیات عملیات</span>
        </ModalHeader>
        <ModalBody>
          <span className="text-primary border-bottom fw-bold">
            اطلاعات کاربر
          </span>

          <Row className="g-2">
            <Col sm="6">
              <InfoItem title="نام">{log?.user?.firstName}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="نام خانوادگی">{log?.user?.lastName}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="کدملی">{log?.user?.nationalNumber}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="وضعیت کاربر">
                <Badge color={log?.user?.isConfirm ? "success" : "danger"}>
                  {log?.user?.isConfirm ? "فعال" : "غیرفعال"}
                </Badge>
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="نقش">
                {convertRoleToText(log?.user?.role)}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="شماره موبایل">
                {log?.user?.phoneNumber}{" "}
                <VerifiedBadge isVerified={log?.user?.phoneNumberVerified} />
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="پست الکترونیک">
                {log?.user?.email}
                <VerifiedBadge isVerified={log?.user?.emailVerified} />
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="تاریخ تایید قوانین و مقررات">
                {convertDate(
                  log?.user?.termsAcceptedAt,
                  "YYYY/MM/DD - HH:mm:ss"
                )}
              </InfoItem>
            </Col>
          </Row>
          <span className="text-primary border-bottom fw-bold">
            اطلاعات عملیات
          </span>
          <Row className="g-2">
            <Col sm="6">
              <InfoItem title="بخش">
                {convertLogUrlToText(log.pathname)}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="تاریخ">
                {convertDate(log.createdAt, "YYYY/MM/DD - HH:mm:ss")}
              </InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="عملیات">
                <Badge
                  className="badge-dim"
                  color={convertLogMethodToColor(log.method)}
                >
                  {convertLogMethodToText(log.method)}
                </Badge>
              </InfoItem>
            </Col>
            <Col xs="12">
              <InfoItem title="مسیر">{log.pathname}</InfoItem>
            </Col>
            <Col xs="12">
              <InfoItem title="آدرس">{log.url}</InfoItem>
            </Col>
            {log?.description && (
              <Col xs="12">
                <InfoItem title="توضیحات">{log.description}</InfoItem>
              </Col>
            )}
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LogsAdmin;
